import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link, useParams } from 'react-router-dom';
import useMount from '../../../../../common/hooks/useMount';
import Loader from '../../../../../common/widgets/Loader';
import { RedirectCategoryTypes } from '../../../enums';
import { Category } from '../../../models/Categories';
import { resources } from '../../../resources/resources';
import { ICategorySideMenuItem } from '../../BenefitsPage/types';
import { mapToSideMenuItems } from '../../BenefitsPage/utils';

interface CategorySideMenuProps {
  rootCategory?: Category;
  handleCategoryChanged?: (categoryName: string) => void;
  forcedCategoryId?: string;
}

export default function CategoriesSideMenu(
  props: Readonly<CategorySideMenuProps>,
) {
  const params = useParams<{ categoryId?: string }>();
  const categoryId = props.forcedCategoryId || params.categoryId;
  const { rootCategory, handleCategoryChanged } = props;
  const { t: translate } = useTranslation();
  const [currentCategory, setCurrentCategory] = useState<Category>();

  useMount(() => {
    const category = getCurentCategory(rootCategory);

    if (category && handleCategoryChanged) {
      handleCategoryChanged(category!.name);
      setCurrentCategory(category);
    }
  });

  const getCurentCategory = (categoryRoot?: Category): Category | undefined => {
    if (categoryRoot && categoryId) {
      for (const category of categoryRoot.children) {
        if (category.id === Number(categoryId)) {
          return category;
        }

        if (category.children && category.children.length > 0) {
          const result = getCurentCategory(category);

          if (result) return result;
        }
      }
    }

    return undefined;
  };

  useEffect(() => {
    const category = getCurentCategory(rootCategory);
    if (category && handleCategoryChanged) {
      handleCategoryChanged(category.name);
      setCurrentCategory(category);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, rootCategory]);

  return (
    <Loader loading={!props.rootCategory} loader={<Skeleton />}>
      <div className="action-panel">
        <div className="action-panel-title">
          <span>{translate(resources.categories)}</span>
        </div>
        <div className="action-panel-content secondary-nav">
          <ul>
            {_.map(
              mapToSideMenuItems(
                (props.rootCategory?.children ?? []).filter(
                  category => !category.isSaveEveryday,
                ),
                Number(categoryId),
              ),
              (item: ICategorySideMenuItem) => {
                return !item.isSelected &&
                  !_.some(item.children, c => {
                    return c.isSelected === true;
                  }) ? (
                  <li key={`${item.id}_${item.title}`}>
                    <Link to={getRedirectLink(item)}>{item.title}</Link>
                  </li>
                ) : (
                  <li key={`${item.id}_${item.title}`} className="active">
                    <Link to={getRedirectLink(item)}>{item.title}</Link>
                    {currentCategory &&
                      (currentCategory.id === item.id ||
                        item.children?.some(
                          it => it.id === currentCategory.id,
                        )) && (
                        <ul>
                          {_.map(item.children, ch => (
                            <li
                              key={ch.id}
                              className={`${ch.isSelected ? 'active' : ''}`}
                            >
                              <Link to={getRedirectLink(ch)}>{ch.title}</Link>
                            </li>
                          ))}
                        </ul>
                      )}
                  </li>
                );
              },
            )}
          </ul>
        </div>
      </div>
    </Loader>
  );
}

const getRedirectLink = (item: ICategorySideMenuItem): string => {
  const currentBenefitTypePrefix = location.pathname.split('/')[1];

  if (!item.redirectCategoryId) {
    return `/${currentBenefitTypePrefix}/${item.id}`;
  }

  if (item.redirectType === RedirectCategoryTypes.Flexible) {
    return `/flexible-benefits/${item.redirectCategoryId}`;
  }

  if (item.redirectType === RedirectCategoryTypes.Club) {
    return `/club-benefits/${item.redirectCategoryId}`;
  }

  return '';
};
